import { NewButton } from '@/components/basic';
import { CalendarsPanel } from '@/components/partial/ResourceSettingsPanel/CalendarsPanel';
import { GeneralPanel } from '@/components/partial/ResourceSettingsPanel/GeneralPanel';
import { HoursPanel } from '@/components/partial/ResourceSettingsPanel/HoursPanel';
import { IntegrationsExternalIdsPanel } from '@/components/partial/ResourceSettingsPanel/IntegrationsExternalIdsPanel';
import { MapPanel } from '@/components/partial/ResourceSettingsPanel/MapPanel';
import RestrictionsTab from '@/components/resources/tabs/access-restrictions/RestrictionsTab';
import LoadingSpinner from '@/components/util/LoadingSpinner';
import { useResourceSettingsState } from '@/hooks/useResourceSettings';
import { useS3Upload } from '@/hooks/useS3Upload';
import { api } from '@/lib/api/api';
import { classNames } from '@/lib/classNames';
import { hasEnabledDeleteResource } from '@/lib/featureFlags';
import { EntityType } from '@/lib/types/EntityType';
import { toEntity } from '@/lib/utils';
import { EditResourceRequest, IResource } from '@gettactic/api';
import { useAreas } from '@gettactic/hooks';
import { useAuthenticated } from '@gettactic/hooks';
import { TrashIcon } from '@heroicons/react/outline';
import { DesktopComputerIcon, ViewGridIcon } from '@heroicons/react/solid';
import { useQueryClient } from '@tanstack/react-query';
import React, { BaseSyntheticEvent, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { IoLayersSharp } from 'react-icons/io5';
import { MdLocalParking } from 'react-icons/md';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '@v2/components/ui/tabs';
import { UploadDropzone } from '@v2/components/upload/UploadDropzone/UploadDropzone';
import { Image } from '@v2/components/display/Image/Image';
import { Button } from '@v2/components/ui/button';
import { useIsDesignSystem2 } from '@webV2/helpers/useIsDesignSystem2';
import { getResourceCoverUrl } from '@gettactic/helpers/src/resources/getResourceCoverUrl';
import { getHelperBubbleSpacing } from '../../../utils/getHelperBubbleSpacing';
import {AlertTriangleIcon} from "lucide-react";

export interface ResourceSettingsPanelProps {
  onEdit: (hasError: boolean) => void;
  onDeleteClick: () => void;
  onCancel: () => void;
}

const entityIconProps = Object.freeze({
  className: 'h-10 w-10 text-white'
});
const EntityIcons: Record<EntityType, JSX.Element> = Object.freeze({
  workspace: <DesktopComputerIcon {...entityIconProps} />,
  meeting_room: <ViewGridIcon {...entityIconProps} />,
  parking: <MdLocalParking {...entityIconProps} />,
  area: <IoLayersSharp {...entityIconProps} />,
  zone: <DesktopComputerIcon {...entityIconProps} />
});

export interface EditResourceForm
  extends Pick<
    IResource,
    'name' | 'label' | 'description' | 'total_slots' | 'is_approvable'
  > {
  maximum_slots_per_user: IResource['maximum_slots_per_user'] | null;
  parent_id: {
    value: IResource['parent_id'];
    label: IResource['name'];
  } | null;
  multiplicity: 'single' | 'multiple';
  is_open: boolean;
  is_unavailable: boolean;
  map_url: IResource['map_url'] | null;
  map_file: File | null;
  amenities: string[];
}

export function getResourceType(entity: EntityType, isMultiple: boolean) {
  switch (entity) {
    case 'parking':
      return isMultiple ? 'parking_lot' : 'parking_space';
    case 'workspace':
      return isMultiple ? 'workspace' : 'desk';
    case 'meeting_room':
      return entity;
    case 'area':
    case 'zone':
      return 'area';
    default: {
      const exhaustiveCheck: string = entity;
      throw new Error(`Not handled entity type ${exhaustiveCheck}`);
    }
  }
}

export const ResourceSettingsPanel = ({
  onEdit,
  onDeleteClick,
  onCancel
}: ResourceSettingsPanelProps) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const refSubmit = useRef<HTMLButtonElement>(null);
  const resourceContext = useResourceSettingsState();
  const resource = resourceContext.state.selected;

  const entity = resource
    ? toEntity(resource.type) || 'workspace'
    : 'workspace';
  const isMeetingRoom = entity === 'meeting_room';
  const queryClient = useQueryClient();
  const resourceOffice =
    authenticatedUser.offices?.byId[resource?.office_id ?? ''];
  const { uploadToS3 } = useS3Upload('/api/uploads/map', true);
  const { areas: areaList } = useAreas(resource?.office_id ?? 'no-id');
  const isV2 = useIsDesignSystem2();

  const parentIdOption = useMemo<EditResourceForm['parent_id']>(() => {
    return resource?.parent_id && areaList?.byId[resource.parent_id]
      ? {
          value: resource.parent_id,
          label: areaList.byId[resource.parent_id].name
        }
      : null;
  }, [resource, areaList]);

  const defaultValues: EditResourceForm = {
    name: resource?.name ?? '',
    label: resource?.label ?? '',
    description: resource?.description ?? '',
    multiplicity: resource
      ? !isMeetingRoom && resource.total_slots > 1
        ? 'multiple'
        : 'single'
      : 'single',
    parent_id: parentIdOption,
    total_slots: resource?.total_slots ?? 1,
    maximum_slots_per_user: resource?.maximum_slots_per_user ?? null,
    is_open: resourceContext.state.selected?.is_open || false,
    is_approvable: resource?.is_approvable ?? false,
    is_unavailable: !(resource?.is_available ?? true),
    map_url: resource?.map_url ?? null,
    map_file: null,
    amenities: resource?.tags ? resource.tags.map((tag) => tag.id) : []
  };
  const form = useForm<EditResourceForm>({
    defaultValues
  });

  const { handleSubmit, setValue, watch } = form;

  const isMultiple = watch('multiplicity') === 'multiple';

  const [showUpload, setShowUpload] = React.useState(false);
  const [loading, setLoading] = useState<boolean>(false);

  const onEditSubmit = async (
    data: EditResourceForm,
    event?: BaseSyntheticEvent<unknown, HTMLFormElement, HTMLFormElement>
  ) => {
    if (!resource) {
      return;
    }
    if (event?.target && event.target.id !== 'resourceSettingsPanel') {
      return;
    }

    setLoading(true);

    let mapUrlNew = data.map_url;
    if (data.map_file) {
      const uploadRes = await uploadToS3(data.map_file, {
        organizationId: authenticatedUser.organization?.id ?? '',
        resourceId: resource.id,
        officeId: resource.office_id
      });
      setValue('map_file', null);
      mapUrlNew = uploadRes.url;
    }

    const resourceType = getResourceType(entity, isMultiple);

    const request: EditResourceRequest = {
      type: resourceType,
      parent_id: data.parent_id?.value ? data.parent_id.value : null,
      name: data.name,
      total_slots:
        resource.type !== 'area'
          ? isMultiple || isMeetingRoom
            ? data.total_slots
            : 1
          : resource.total_slots,
      maximum_slots_per_user: data.maximum_slots_per_user ?? 1,
      is_open: data.is_open,
      is_approvable: data.is_approvable,
      is_available: !data.is_unavailable,
      // TODO: New field that we'll be exposing in the UI
      // for now we'll set it to whatever it's currently set to
      is_shareable: resource.is_shareable,
      weight: resource.weight || 0,
      map_url: mapUrlNew,
      label: data.label,
      description: data.description
    };

    const { ok } = await api.client.organizations.resourcePut(
      resource.id,
      request
    );

    if (isV2) {
      await api.client.tags.removeAllTagsFromResource(resource.id);

      const hasAmenities = data.amenities.length > 0;

      if (hasAmenities) {
        await api.client.tags.addTagToResource(
          resource.id,
          data.amenities.map((id) => ({ tag_id: id }))
        );
      }
    }

    setLoading(false);
    if (ok) {
      toast.success(resource ? 'Changes saved' : 'Created successfully');
    } else {
      toast.error("Sorry we couldn't save the changes");
    }
    onEdit(!ok);
  };

  const tabs: {
    id: string;
    title: string;
    condition: boolean;
    render: () => JSX.Element | null;
  }[] = [
    {
      id: 'general',
      title: 'General',
      condition: true,
      render: () => (
        <GeneralPanel
          resource={resourceContext.state.selected}
          refSubmit={refSubmit}
          entity={entity}
          areaList={areaList}
          defaultValues={defaultValues}
          form={form}
        />
      )
    },
    {
      id: 'hours',
      title: 'Hours',
      condition: resource?.type !== 'area',
      render: () =>
        resourceOffice && resource ? (
          <HoursPanel resources={[resource]} office={resourceOffice} />
        ) : null
    },
    {
      id: 'restrictions',
      title: 'Access & Restrictions',
      condition: resource?.type !== 'area',
      render: () =>
        resource ? (
          <RestrictionsTab mutateAll={onEdit} resources={[resource]} />
        ) : null
    },
    {
      id: 'integrations',
      title: 'Integrations',
      condition: resource?.type !== 'area',
      render: () =>
        resourceOffice && resource ? (
          <IntegrationsExternalIdsPanel
            resources={[resource]}
            office={resourceOffice}
          />
        ) : null
    },
    {
      id: 'tab-map',
      title: 'Map',
      condition: resource?.type === 'area',
      render: () => (
        <MapPanel watch={watch} setValue={setValue} loading={loading} />
      )
    },
    {
      id: 'calendars',
      title: 'Calendars',
      condition: resource?.type === 'meeting_room',
      render: () => (resource ? <CalendarsPanel resource={resource} /> : null)
    }
  ];
  const visibleTabs = useMemo(
    () => tabs.filter((tabDef) => tabDef.condition),
    [tabs]
  );
  const [selectedTab, setSelectedTab] = useState(visibleTabs[0]?.id || '');

  const baseResource = resource ? getResourceCoverUrl(resource) : undefined;
  const [resourceMediaUrl, setResourceMediaUrl] = useState(baseResource);

  return (
    <form
      id="resourceSettingsPanel"
      className="flex h-full flex-auto flex-col flex-nowrap"
      onSubmit={handleSubmit(onEditSubmit)}
    >
      <div className={'flex flex-col'}>
        {isV2 ? (
          <>
            {showUpload && resource?.id ? (
              <>
                <UploadDropzone
                    type="images"
                    onUpload={async (file, setProgress) => {
                      const res =
                          await api.client.organizations.resourceMediaUpload(
                              resource.id,
                              file,
                              true,
                              setProgress
                          );
                      const newImage = res.result
                          ? getResourceCoverUrl(
                              res.result as unknown as IResource,

                              // 94 is from image size of w-24
                              94,
                              true
                          )
                          : resourceMediaUrl;
                      setResourceMediaUrl(newImage);
                    }}
                    onUploadComplete={() => {
                      toast.success('Image uploaded');
                      setShowUpload(false);
                      onEdit(false);
                    }}
                    autoUpload
                />
                <div className={"w-full flex items-center justify-center bg-neutral-100 py-2 gap-2 rounded-md"}><AlertTriangleIcon className={"size-5"} /> Reccomended image size is 812px by 480px</div>
              </>
            ) : (
              <div className={'flex mx-auto items-center gap-6'}>
                {resourceMediaUrl ? (
                  <div
                    className={
                      'overflow-hidden h-24 w-24 shadow-sm border-1 items-center justify-center border-neutral-600 rounded-lg'
                    }
                  >
                    <Image
                      src={resourceMediaUrl}
                      wrapperClassName={
                        'h-full w-full object-cover object-center'
                      }
                      className={'h-full w-full object-cover object-center'}
                    />
                  </div>
                ) : (
                  <div className="flex h-24 w-24 flex-none items-center justify-center rounded-4xl bg-secondary-hover [.tactic-v2_&]:bg-primary">
                    {EntityIcons[entity]}
                  </div>
                )}
                {resource?.id && (
                  <Button
                    variant={'secondary'}
                    onClick={() => setShowUpload(true)}
                  >
                    Change Image
                  </Button>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="flex h-24 w-24 flex-none items-center justify-center rounded-4xl bg-secondary-hover [.tactic-v2_&]:bg-primary">
            {EntityIcons[entity]}
          </div>
        )}
      </div>

      <div className={'flex-1 overflow-auto'}>
        <Tabs
          value={selectedTab}
          onValueChange={setSelectedTab}
          defaultValue={visibleTabs[0]?.id}
        >
          <TabsList aria-label="Resource tabs" variant="settingsTabs">
            {visibleTabs.map((tabDef) => (
              <TabsTrigger
                key={tabDef.id}
                variant="minimalistic"
                value={tabDef.id}
                type="button"
              >
                {tabDef.title}
              </TabsTrigger>
            ))}
          </TabsList>
          {visibleTabs.map((tabDef) => (
            <TabsContent key={tabDef.id} value={tabDef.id}>
              {selectedTab === tabDef.id && tabDef.render()}
            </TabsContent>
          ))}
        </Tabs>
      </div>

      <div
        className={classNames(
          'flex flex-nowrap gap-3 pt-4',
          ['general', 'tab-map'].indexOf(selectedTab ?? '') === -1
            ? 'hidden'
            : '',
          getHelperBubbleSpacing()
        )}
      >
        <NewButton.Component
          className="w-full border-input sm:w-auto sm:flex-1"
          variant={NewButton.Variant.PrimarySquared}
          theme={NewButton.Theme.White}
          size={NewButton.Size.Medium}
          onClick={() => onCancel()}
        >
          Close
        </NewButton.Component>
        <NewButton.Component
          ref={refSubmit}
          className="w-full sm:w-auto sm:flex-1"
          variant={NewButton.Variant.PrimarySquared}
          theme={NewButton.Theme.Primary}
          size={NewButton.Size.Medium}
          type="submit"
          form="resourceSettingsPanel"
          disabled={loading}
        >
          {loading ? <LoadingSpinner color="text-white" /> : <>Save</>}
        </NewButton.Component>
        {hasEnabledDeleteResource(
          authenticatedUser,
          resource ? resource : undefined
        ) ? (
          <NewButton.Component
            className="w-full max-w-[58px] flex-none sm:w-auto"
            variant={NewButton.Variant.PrimarySquared}
            theme={NewButton.Theme.Red}
            size={NewButton.Size.Medium}
            disabled={loading}
            onClick={onDeleteClick}
          >
            <TrashIcon className="h-6 w-6" />
          </NewButton.Component>
        ) : null}
      </div>
    </form>
  );
};
